import { FC, useEffect, useMemo, useState } from 'react'
import { Column } from 'react-table'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import i18n from 'i18next'
import { useTranslation } from 'react-i18next'

import Search from 'components/AdvancedTable/AdvTableSearchComponent'
import Box from 'components/Box'
import Text from 'components/Text'
import Toast from 'components/Toast'
import Tooltip from 'components/Tooltip'
import Table, { PaginationType } from 'components/Table'
import { DateWithToolTip, Icon } from 'components'
import { DateTime, LastActivity, LeadInfoWrapper, LeadOwner } from './styled-comps'

import useWorkFlowLookup from 'hooks/lookup/use-workflow-lookup'
import useCustomLeadForm from 'hooks/use-custom-lead-form'
import { useReusableTab } from 'hooks/use-reusable-lead-tab'

import leadQuery from 'queries/lead'
import { GetLeadsPayload } from 'services/types/lead'
import { FIELD_TYPE_ACCESSOR, LEAD_LOOK_UP_TAB } from 'utils/constants'
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from './constant'
import { ACTIVITY_TOOLTIP } from 'modules/Leads/LeadDetailV2/RightSection/ActivitiesTab/ActivityItemWrapper'
import { LeadInfo } from 'store/types'
import { getLeadRoute } from 'utils/url'
import { getActivityIcon } from 'modules/Leads/hooks/use-column-definitions'

type LeadListProps = {
  accessor?: string
  onSelectRow: (lead?: LeadInfo) => void
  selectedLead?: LeadInfo
}

const DEFAULT_FILTERS: GetLeadsPayload = {
  page: DEFAULT_PAGE,
  per_page: DEFAULT_PAGE_SIZE,
  query: '',
  view: 'ALL',
  filter: {
    lead_type_id: [],
  },
  required_fields: {
    standard_fields: [
      'lead_id, lead_name',
      'lead_type_name',
      'lead_status_name',
      'lead_owner_name, lead_owner_team_name',
      'last_activity_ts, last_activity_type',
      'lead_in_date',
      'is_deleted',
    ],
    custom_fields: [],
  },
  ...{
    sort_by: {
      key: 'create_ts',
      order: 'desc',
    },
  },
}

export const LeadList: FC<LeadListProps> = ({ accessor, onSelectRow, selectedLead }) => {
  const [filters, setFilters] = useState<GetLeadsPayload>(DEFAULT_FILTERS)
  const { leadId = '' } = useParams<{ leadId: string }>()
  const { leadTypes } = useWorkFlowLookup({})
  const { data: customFormFields } = useCustomLeadForm({ is_active: true })
  const { data: leads, isSuccess, isLoading, isFetching } = leadQuery.useLeadList(filters, {
    enabled: filters.filter?.lead_type_id && filters.filter.lead_type_id.length > 0,
  })

  const { t } = useTranslation()
  const { openInReusableTab } = useReusableTab({ tabName: LEAD_LOOK_UP_TAB })

  useEffect(() => {
    if (customFormFields) {
      let leadTypeFilterIds = customFormFields?.find(
        (customFormField) =>
          customFormField.type_accessor === FIELD_TYPE_ACCESSOR.lookup && customFormField.accessor === accessor,
      )?.property?.leadTypes

      if (!leadTypeFilterIds?.length) {
        leadTypeFilterIds = leadTypes?.map((leadType) => leadType.id)
      }

      setFilters({
        ...filters,
        filter: {
          lead_type_id: leadTypeFilterIds,
          view_other_accounts: true,
        },
      })
    }
  }, [customFormFields, leadTypes])

  const handleSearch = (searchValue: string) => {
    setFilters({
      ...filters,
      query: searchValue,
    })
    onSelectRow(undefined)
  }

  const handleChangePagination = ({ pageNumber, pageSize }: PaginationType) => {
    setFilters({
      ...filters,
      page: pageNumber && pageNumber <= 0 ? 1 : pageNumber,
      per_page: pageSize,
    })
  }

  const activeRowIndex = useMemo(() => {
    if (!selectedLead || !leads?.data) return undefined

    const index = leads.data.findIndex((lead) => lead.leadId === selectedLead.leadId)

    return index >= 0 ? index : undefined
  }, [leads?.data, selectedLead?.leadId])

  const handleRowClick = (row: LeadInfo) => {
    if (row.leadId === leadId) {
      Toast.toastMessage({
        message: `${i18n.t('oops_lbl')} ${i18n.t('unable_to_reference_itself_error')}`,
        variant: 'error',
      })

      return
    }

    if (row.status?.toLowerCase() === 'deleted') {
      Toast.toastMessage({
        message: `${i18n.t('oops_lbl')} ${i18n.t('unable_to_reference_deleted_lead_error')}`,
        variant: 'error',
      })

      return
    }

    onSelectRow(row)
  }

  const columns: Array<Column<LeadInfo>> = useMemo(
    () => [
      {
        id: 'leadName',
        Header: 'lead_name_lbl',
        accessor: 'leadName',
        width: 200,
        Cell: ({ row }) => {
          const leadURI = getLeadRoute({ leadId: row.original.leadId })

          return (
            <LeadInfoWrapper
              to={leadURI}
              onClick={(e) => {
                e.preventDefault()
                openInReusableTab(leadURI)
              }}
            >
              <Box className="data-cell" width={'100%'}>
                <div
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <Tooltip title={row.original.leadName}>
                    <Text fontSize={14} lineHeight="24px" ellipsis>
                      {row.original.leadName || '-'}
                    </Text>
                  </Tooltip>
                </div>
              </Box>
            </LeadInfoWrapper>
          )
        },
        disableSortBy: true,
      },
      {
        id: 'leadType',
        Header: 'lead_type_lbl',
        accessor: 'leadType',
        Cell: ({ row }) => (row.original?.leadType ? row.original.leadType : '-'),
        disableSortBy: true,
      },
      {
        id: 'leadOwner',
        Header: 'lead_owner_title_lbl',
        accessor: 'leadOwner',
        width: 170,
        Cell: ({ row }) => (
          <LeadOwner
            style={{ width: '100%' }}
            color={row.original.leadOwner || row.original.leadOwnerTeam ? 'defaultText' : 'red'}
          >
            {!row.original.leadOwner && row.original.leadOwnerTeam && (
              <Icon color="gray5" icon={['far', 'users']} mr="8px" />
            )}
            <Text
              fontSize={14}
              lineHeight="24px"
              ellipsis
              color={row.original.leadOwner || row.original.leadOwnerTeam ? 'defaultText' : 'red'}
            >
              {row.original.leadOwner || row.original.leadOwnerTeam || t('unassigned_lbl')}
            </Text>
          </LeadOwner>
        ),
        disableSortBy: true,
      },
      {
        id: 'status',
        Header: 'status_lbl',
        accessor: 'status',
        Cell: ({ row }) => row.original.status || '-',
        disableSortBy: true,
      },
      {
        id: 'lastActivity',
        Header: 'last_activity_title',
        accessor: 'lastActivity',
        width: 235,
        Cell: ({ row }) =>
          row.original?.lastActivity ? (
            <LastActivity
              style={{
                width: '100%',
                fontWeight: 400,
              }}
            >
              <Box display="flex" mb="0.2rem" width={'100%'}>
                {row.original.lastActivity.lastActivityType && (
                  <Tooltip
                    textTransform="capitalize"
                    title={ACTIVITY_TOOLTIP[row.original.lastActivity.lastActivityType]}
                    position="bottom"
                    fontWeight="normal"
                  >
                    {getActivityIcon(row.original.lastActivity.lastActivityType)}
                  </Tooltip>
                )}

                <DateTime>
                  <DateWithToolTip date={row.original.lastActivity?.lastActivityTs} fontWeight="normal" />
                </DateTime>
              </Box>
              <Tooltip title={row.original.lastActivity?.lastActivityStatus} position="bottom">
                <Text fontWeight="normal">{row.original.lastActivity?.lastActivityStatus}</Text>
              </Tooltip>
            </LastActivity>
          ) : (
            '-'
          ),
        disableSortBy: true,
      },
      {
        id: 'leadInDate',
        Header: 'lead_in_date_title',
        accessor: 'leadInDate',
        width: 230,
        Cell: ({ row }) => (
          <DateTime style={{ width: '100%' }}>
            <DateWithToolTip date={row.original.leadInDate} fontWeight="normal" />
          </DateTime>
        ),
        disableSortBy: true,
      },
    ],
    [t, openInReusableTab],
  )

  return (
    <>
      <Styles>
        <Box
          position="relative"
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          height="64px"
          backgroundColor="gray1"
          paddingX={2}
          width={'100%'}
        >
          <Box className="search-box">
            <Search setSearchValue={handleSearch} />
          </Box>
        </Box>
      </Styles>
      <Table
        columns={columns}
        uniqueTableName="userlisting.dataModal"
        data={isSuccess ? (leads.data as any) : []}
        totalDataCount={isSuccess ? Number(leads.count) : 0}
        pageSize={filters.per_page}
        isLoading={isLoading || isFetching}
        onRowClick={handleRowClick}
        onChangePagination={handleChangePagination}
        pageNumber={filters.page}
        size="medium"
        emptyText="lead"
        style={{ maxWidth: '1200px' }}
        activeRowIndex={activeRowIndex}
      />
    </>
  )
}

const Styles = styled.div`
  position: relative;
  width: 100%;

  .data-cell {
    display: flex;
    align-items: center;
    height: 100%;
  }

  .search-box {
    input {
      padding-right: 35px;
    }
  }
`

export default LeadList
